import { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCompanies, usePendingKPIs } from "@/hooks/useCompanyData";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: companies } = useCompanies();
  const firstCompany = companies?.[0];
  const { data: pending } = usePendingKPIs(firstCompany?.id);

  const totalPending = pending?.pending ?? 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-xl relative">
          <Bell className="h-5 w-5" />
          {totalPending > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-danger text-[10px] font-bold text-white flex items-center justify-center">
              {totalPending}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-2xl p-0">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {totalPending > 0 && firstCompany ? (
            <button
              className="w-full text-left p-4 hover:bg-muted/50 transition-colors border-b last:border-0"
              onClick={() => {
                setOpen(false);
                navigate(`/app/companies/${firstCompany.id}/submit`);
              }}
            >
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm">⏳</span>
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {totalPending} KPI{totalPending !== 1 ? "s" : ""} pending this week
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {pending?.names.slice(0, 3).join(", ")}
                    {(pending?.names.length ?? 0) > 3 && ` +${(pending?.names.length ?? 0) - 3} more`}
                  </p>
                  <p className="text-xs text-primary mt-1 font-medium">Quick submit →</p>
                </div>
              </div>
            </button>
          ) : (
            <div className="p-8 text-center text-muted-foreground text-sm">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              All caught up! 🎉
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
